// The image maker: paint a picture from words and pictures to copy from, or change one. It knows nothing about episodes
// or the library; the caller says what the references are, what can be started from, and how to paint. One page for
// timeline pictures and for the pictures of characters, places and props (`mode`). It is the Picture medium of the
// Maker (src/ui/maker), so it has the same shape as the voice and animation pages.
// Top to bottom: the drawing style (the last one painted with is picked), who and what is in it, "Start from" (Blank,
// then every version, newest first), the words with the Draw button, and the picked picture, big. Starting from Blank
// paints something new; starting from a picture changes that picture using only the words just typed. Each result
// joins "Start from" and is picked, so changes can be made one after another. Whatever is picked is in use: tapping a
// version or painting a new one tells the caller (`onPick`, or its `paint`), which saves it, so there is nothing to
// confirm. Tapping Blank only means the next picture is new: the picture in use stays, and the big area shows an empty
// frame until the new one arrives. Painting carries on if the page is left (`jobKey`, see ui/jobs.ts): the caller's
// `paint` saves its result wherever it goes, and the page shows it is still drawing when opened again. A painting that
// goes wrong leaves a red "Try again" tile.
// A bin on the big picture deletes that version (`onDelete`, with a question first if `confirmDelete`); it is hidden
// when only one version is left.
// With `face`, the talking faces are drawn on the picture: a character's one face (`start.face`), or the faces of
// everyone in a shot (`start.faces`). If it has `onChange`, a small button under the picture opens the caller's page for
// placing them ("Change face" / "Change faces"). Places and props have no face.
// With `bare`, it is only the sections, to sit inside a stage page (src/ui/stages) that brings the top bar, footer and
// toast; what would be a toast goes to `onNote`.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { notify, runJob, useJob } from "../jobs";
import { imageSrc } from "../../library/images";
import { uid } from "../../model/ids";
import type { Picture } from "../../model/episode";
import type { FaceSpots } from "../../model/face";
import type { AssetKind } from "../../model/library";
import type { PaintSource } from "../../model/timeline";
import { MAX_STYLE_NAME, MAX_STYLE_TEXT, type PaintStyle } from "../../model/styles";
import { pictureFit } from "../../engine/camera";
import { isBlinking } from "../../engine/face";
import { faceImages, pictureFrame } from "../../engine/frame";
import { FrameSvg } from "../../render/FrameSvg";
import { Body, Confirm, DoneFooter, Icon, Layer, ModelSelect, Page, Section, Toast, TopBar, useIdleClock } from "../common";
import { useFootPlain } from "../stages/StagePage";
import { MakeRow, MakerPage, ResultFrame, VersionStrip } from "../maker/Maker";
import { api } from "../api";
import { useDraft } from "../drafts";
import { lastStyleId, makeStyleFaces, rememberStyle, saveCustomStyle, styleById, useStyles } from "./styles";
import "./imageMaker.css";

export type MakerMode = "shot" | "character" | "place" | "prop";
// A picture to start from: an earlier version ("V2") or, on the timeline, another picture (`other`: not one of this
// picture's own versions, so it is never numbered or deleted here).
export type MakerStart = { id: string; label: string; picture: Picture; face?: FaceSpots; faces?: MakerFace[]; other?: boolean };
// One of a shot's faces: whose it is (`key`), where it goes and its face style.
export type MakerFace = { key: string; face: FaceSpots; style?: string };
// One group of reference pictures. A group of an asset kind can have a "New …" tile (if the caller handles `onNew`).
export type MakerGroup = { key: string; title: string; kind?: AssetKind; items: PaintSource[] };
// start: the picture to change (none: paint from Blank). style: the drawing style's words.
export type MakerRequest = { start?: MakerStart; refs: PaintSource[]; text: string; style: string; styleId: string };
// What the caller made of a painting: the new start (already in `starts`, or about to be), and anything to tell the person.
export type MakerPainted = { start: MakerStart; note?: string };

const COPY: Record<AssetKind, { noun: string; empty: string; quiet: string }> = {
  character: { noun: "character", empty: "No characters yet. Make one and you can put them in any picture.", quiet: "" },
  background: { noun: "place", empty: "", quiet: "No places yet." },
  prop: { noun: "prop", empty: "", quiet: "No props yet." },
};
const EXAMPLE: Record<MakerMode, string> = {
  shot: "e.g. A kitchen seen from low down. A cat sits on the counter, swinging its tail.",
  character: "e.g. A grumpy old man in a tracksuit two sizes too small, bald, with a huge grey moustache.",
  place: "e.g. A cramped 1970s kitchen at night, a dripping tap, a fridge covered in magnets.",
  prop: "e.g. A battered red toaster with a frayed cable.",
};
const KIND: Record<MakerMode, AssetKind | undefined> = { shot: undefined, character: "character", place: "background", prop: "prop" };
const NOUN: Record<MakerMode, string> = { shot: "picture", character: "character", place: "place", prop: "prop" };
const SHAPE: Record<MakerMode, number> = { shot: 9 / 16, character: 2 / 3, place: 9 / 16, prop: 1 }; // a picture not painted yet
const PAINT_MS = 20_000; // how long the progress bar takes to fill

export const sameRef = (a: PaintSource, b: PaintSource) => a.src === b.src && a.kind === b.kind;
const list = (names: string[]) => (names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

export function ImageMaker({ mode, title, sub, refsTitle, starts, startId: inUse, face, refs, setRefs, groups, paint, onNew, onEdit, onPick, onDelete, confirmDelete, onClose, footer, jobKey, bare, onNote, draftKey }: {
  mode: MakerMode; title?: string; sub?: string;
  refsTitle: string; // heading over the Characters / Places / Props cards; each screen words it for itself
  starts: MakerStart[]; startId: string | null; // pictures to start from besides Blank, and the one in use (picked when it changes)
  face?: { style?: string; onChange?: (s: MakerStart) => void }; // talking faces: one on each start's `face`, or several on its `faces`
  refs: PaintSource[]; setRefs: (f: (rs: PaintSource[]) => PaintSource[]) => void;
  groups: MakerGroup[];
  paint: (req: MakerRequest) => Promise<MakerPainted>;
  onNew?: (kind: AssetKind) => void;
  onEdit?: (r: PaintSource) => void; // a pencil on each picked character, place or prop opens its form
  onPick: (s: MakerStart) => void; // a start was tapped: it is now the one in use
  onDelete?: (s: MakerStart) => void; // the bin on the picked version; the caller puts another in use if it was the one
  confirmDelete?: boolean; // ask before deleting (library pictures); on the timeline it is immediate, and undoable
  onClose?: () => void;
  footer?: ReactNode; // what comes next, at the foot of the page
  jobKey?: string; // painting runs under this key, so it shows here again if the page is left and opened again
  bare?: boolean; onNote?: (message: string | null) => void; // see above
  draftKey?: string; // the description typed but not drawn yet is kept under this, so it survives leaving the page (ui/drafts.ts)
}) {
  const [startId, setStartId] = useState<string | null>(inUse);
  useEffect(() => { setStartId(inUse); }, [inUse]);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const [made, setMade] = useState<MakerStart | null>(null); // the newest result, until the caller's `starts` has it
  const [text, setText] = useDraft(draftKey, "");
  const [own, setBusy] = useState<number | null>(null); // when painting began here (without a job key)
  const busy = useJob(jobKey) ?? own;
  const [error, setErrorHere] = useState<string | null>(null);
  const setError = (m: string | null) => (bare ? onNote?.(m) : setErrorHere(m));
  const [failed, setFailed] = useState<{ req: MakerRequest; words: string } | null>(null); // the painting that went wrong, to try again
  const [deleting, setDeleting] = useState<MakerStart | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const picBox = useRef<HTMLDivElement>(null);
  const [scrollTick, setScrollTick] = useState(0);
  const [picking, setPicking] = useState<string | null>(null); // the group whose picker page is open
  const styles = useStyles();
  const [styleId, setStyleId] = useState(lastStyleId);
  const style = styleById(styles, styleId);
  const [choosingStyle, setChoosingStyle] = useState(false);

  const start = starts.find(s => s.id === startId) ?? (made?.id === startId ? made : undefined);
  // A version deleted from under the page: back to the one in use.
  useEffect(() => { if (startId && !start) setStartId(inUse); }, [startId, start, inUse]);
  const changing = !!start;
  const painting = busy !== null;
  const count = starts.filter(s => !s.other).length; // this picture's own versions
  const next = `V${count + 1}`; // what the next drawing will be called

  // A character, place or prop rarely copies another picture: it shows only Style, with "look like another one" as a small
  // optional button that opens a card of its own kind only. A shot shows every group under References.
  const lite = mode !== "shot";
  const [copying, setCopying] = useState(false);
  const ownKind = KIND[mode];
  const shownGroups = lite ? groups.filter(g => g.kind === ownKind || refs.some(r => g.items.some(x => sameRef(x, r)))) : groups;
  const showCopy = !lite || copying || refs.length > 0;

  const toggle = (r: PaintSource) => setRefs(rs => (rs.some(x => sameRef(x, r)) ? rs.filter(x => !sameRef(x, r)) : [...rs, r]));
  const insertName = (name: string) => {
    const el = area.current;
    const at = el ? el.selectionStart : text.length;
    const before = text.slice(0, at), after = text.slice(at);
    const next = `${before}${before && !before.endsWith(" ") ? " " : ""}${name}${after.startsWith(" ") ? "" : " "}${after}`;
    setText(next);
    requestAnimationFrame(() => { el?.focus(); const c = next.length - after.length; el?.setSelectionRange(c, c); });
  };

  const names = (k: PaintSource["kind"]) => refs.filter(r => r.kind === k).map(r => r.name);
  const who = names("character"), place = names("background")[0], thing = names("prop")[0];
  const placeholder = mode !== "shot"
    ? changing ? "e.g. Make the jumper green." : EXAMPLE[mode]
    : changing
      ? `e.g. Make the sky orange${who.length ? `, and put ${list(who)} on the left` : thing ? `, and put the ${thing} on the table` : ""}.`
      : who.length
        ? `e.g. ${list(who)} ${who.length > 1 ? "stand" : "stands"} ${place ? `in ${place}` : "in front of a window"}, looking surprised.`
        : place ? `e.g. ${place} at night, seen from low down.` : EXAMPLE.shot;

  const words = text.trim();
  const why = painting ? "" : changing ? (words ? "" : "Say what should change first.") : (words || (mode === "shot" && refs.length) ? "" : mode === "shot" ? "Pick who and what should be in it, or describe it." : "Describe it first.");

  // After painting, bring the new picture into view.
  useEffect(() => { if (scrollTick) picBox.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [scrollTick]);

  function make() {
    const sent = words || (mode === "shot" ? `A picture with ${list(refs.map(r => r.name))}.` : "");
    void run({ ...(start ? { start } : {}), refs, text: sent, style: style.text, styleId: style.id }, words);
  }
  // Paints a request (a new one, or the same one again from a red tile). `typed` is what to put back in the box if it fails.
  async function run(req: MakerRequest, typed: string) {
    rememberStyle(req.styleId);
    if (!jobKey) setBusy(Date.now());
    setError(null);
    setFailed(null);
    setText("");
    requestAnimationFrame(() => picBox.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    try {
      const done = await (jobKey ? runJob(jobKey, paint(req)) : paint(req));
      if (!alive.current) return;
      setMade(done.start);
      setStartId(done.start.id);
      if (done.note) setError(done.note);
      setScrollTick(n => n + 1);
    } catch (e) {
      if (!alive.current) return notify(`Couldn't draw the picture: ${(e as Error).message}`);
      setText(typed);
      setFailed({ req, words: typed });
    }
    if (alive.current) setBusy(null);
  }

  const shown = start?.picture;
  // The Make button is the next useful thing once something is typed (or there is no picture yet): then it is the yellow one
  // and the page's footer button turns plain.
  const primary = !painting && (!!words || !shown);
  useFootPlain(primary && !!shown);
  const many = mode === "shot";
  const drawn: MakerFace[] = !face || !start ? [] : many ? start.faces ?? [] : start.face ? [{ key: "face", face: start.face, style: face.style }] : [];
  const canDelete = !!onDelete && !!start && !start.other && count > 1;

  const groupRows = shownGroups.map(g => {
    const picked = refs.filter(r => g.items.some(x => sameRef(x, r)));
    return (
      <button key={g.key} className="pp-row" disabled={painting} onClick={() => setPicking(g.key)} aria-label={`${g.title}: ${picked.length ? picked.map(r => r.name).join(", ") : "none picked"}`}>
        <span className="pp-row-pics">
          {picked.length
            ? picked.slice(0, 3).map(r => <img key={`${r.kind}-${r.src}`} className={r.kind === "character" ? "round" : ""} src={imageSrc(r.src)} alt="" draggable={false} />)
            : <span className="pp-row-none"><RefPlaceholder kind={g.kind ?? "shot"} /></span>}
        </span>
        <span className="pp-row-text"><span>{g.title}</span><b>{picked.length ? picked.map(r => r.name).join(", ") : "None"}</b></span>
        <Icon name="chevron" size={18} />
      </button>
    );
  });

  const sections = (
    <>
          <Section title={lite ? "Style" : "References"} hint={lite ? undefined : "The style to draw in and the pictures to copy from. Call them by name in the description."}>
          <div className="pp-card" role="group" aria-label={lite ? "Style" : refsTitle}>
            <button className="pp-row" disabled={painting} onClick={() => setChoosingStyle(true)} aria-label={`Style: ${style.name}. Tap to change.`}>
              <StyleSample style={style} />
              <span className="pp-row-text"><span>Style</span><b>{style.name}</b></span>
              <Icon name="chevron" size={18} />
            </button>
            {!lite && groupRows}
          </div>
          {lite && (showCopy ? (
            <div className="pp-copy">
              <p className="pp-copy-head"><b>Look like another {NOUN[mode]}</b> <span>Optional</span></p>
              <div className="pp-card" role="group" aria-label={refsTitle}>{groupRows}</div>
            </div>
          ) : (
            <button className="pp-copy-open" disabled={painting} onClick={() => setCopying(true)}>
              <Icon name="plus" size={16} /> Make it look like another {NOUN[mode]} <span>Optional</span>
            </button>
          ))}
          </Section>

          {(starts.length > 0 || painting || failed) && (
            <VersionStrip note disabled={painting}
              fresh={{ label: "Blank", on: !startId, onPick: () => setStartId(null) }}
              making={painting ? { label: next, word: "Drawing" } : undefined}
              failed={failed && !painting ? { label: next, onRetry: () => void run(failed.req, failed.words) } : undefined}
              versions={starts.map(s => ({ id: s.id, label: s.label, src: imageSrc(s.picture.src), on: startId === s.id }))}
              onPick={id => { const s = starts.find(x => x.id === id); if (s) { setStartId(s.id); onPick(s); } }} />
          )}

          <Section title={changing ? `What should change in ${start!.label}?` : "Describe it"}
            hint={changing ? undefined : lite ? `Say what the ${NOUN[mode]} looks like.` : "Say what is in the picture and where. Call the references above by their names."}>
            {refs.length > 0 && (
              <div className="pp-chips">
                {refs.map(r => <button key={`${r.kind}-${r.src}`} className="chip" onClick={() => insertName(r.name)}><Icon name="plus" size={16} /> {r.name}</button>)}
              </div>
            )}
            <textarea ref={area} className="input pp-words" rows={changing ? 2 : 4} maxLength={1200} value={text} disabled={painting} aria-label={changing ? "What should change" : "Describe the picture"}
              placeholder={placeholder} onChange={e => setText(e.target.value)} />
            <MakeRow model plain={!primary} disabled={painting || !!why} onClick={make}>
              <Icon name="sparkle" /> Make {next}
            </MakeRow>
          </Section>

          <div ref={picBox}>
            <ResultFrame tall aspect={shown ? shown.w / shown.h : SHAPE[mode]}
              empty={shown ? undefined : `${next} appears here`}
              busy={busy !== null ? { message: `Drawing ${next}…`, since: busy, ms: PAINT_MS } : undefined}
              onDelete={canDelete ? () => (confirmDelete ? setDeleting(start!) : onDelete!(start!)) : undefined} deleteLabel={`Delete ${start?.label ?? "this picture"}`}>
              {shown && (drawn.length
                ? <FacedPicture picture={shown} faces={drawn} />
                : <img src={imageSrc(shown.src)} alt={start!.label} draggable={false} />)}
            </ResultFrame>
          </div>
          {face?.onChange && start && (many || start.face) && (
            <button className="btn small soft pp-face-btn" disabled={painting} onClick={() => face.onChange!(start)}>
              <Icon name="face" size={18} /> {many ? (drawn.length ? "Change faces" : "Add faces") : "Change face"}
            </button>
          )}
    </>
  );
  const pages = (
    <>
      {picking && (() => {
        const g = groups.find(x => x.key === picking);
        return g && <RefPicker group={g} refs={refs} onToggle={toggle} onNew={onNew} onEdit={g.kind ? onEdit : undefined} onClose={() => setPicking(null)} />;
      })()}
      {choosingStyle && <StylePicker styles={styles} on={style.id} onPick={id => { setStyleId(id); setChoosingStyle(false); }} onClose={() => setChoosingStyle(false)} />}
      {deleting && (
        <Confirm title={`Delete ${deleting.label}?`} yes="Delete" danger onYes={() => { const s = deleting; setDeleting(null); onDelete?.(s); }} onNo={() => setDeleting(null)}>
          This version of the picture is gone for good.
        </Confirm>
      )}
    </>
  );
  if (bare) return <>{sections}{pages}</>;
  return (
    <Layer>
      <MakerPage title={title ?? ""} sub={sub} onBack={onClose} footer={footer} toast={error} clearToast={() => setError(null)} className="pp">{sections}</MakerPage>
      {pages}
    </Layer>
  );
}

// What a references card shows before anything is picked: a rough sketch of a figure, a place, a thing or a picture.
function RefPlaceholder({ kind }: { kind: AssetKind | "shot" }) {
  const art: Record<AssetKind | "shot", ReactNode> = {
    character: <><circle cx="24" cy="14" r="7" /><path d="M13 42c0-9 4-16 11-16s11 7 11 16" /></>,
    background: <><path d="M4 38l12-14 8 9 7-8 13 13" /><circle cx="35" cy="12" r="4" /><path d="M4 42h40" /></>,
    prop: <><path d="M10 18l14-7 14 7v16l-14 7-14-7z" /><path d="M10 18l14 7 14-7M24 25v16" /></>,
    shot: <><rect x="12" y="5" width="24" height="38" rx="3" /><path d="M15 34l6-7 5 5 4-4 3 6" /><circle cx="29" cy="14" r="3" /></>,
  };
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {art[kind]}
    </svg>
  );
}

// A picture with its talking faces on, each blinking now and then, filling its box.
function FacedPicture({ picture, faces }: { picture: Picture; faces: MakerFace[] }) {
  const clock = useIdleClock();
  const fit = pictureFit(picture);
  const view = { x: fit.x, y: fit.y, w: picture.w * fit.s, h: picture.h * fit.s };
  const frame = pictureFrame(picture, undefined, { clock }, view);
  for (const f of faces) frame.images.push(...faceImages(f.face, fit, "neutral", isBlinking(null, f.key, 0, clock), "X", f.style));
  return <FrameSvg id="maker-face" boil={false} frame={frame} />;
}


// A style's sample picture (the same beach scene for every style), or a blank tile until it has one.
export function StyleSample({ style, big }: { style: Pick<PaintStyle, "sample">; big?: boolean }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`pp-sample${big ? " big" : ""}`}>
      {style.sample && !broken ? <img src={imageSrc(style.sample)} alt="" draggable={false} onError={() => setBroken(true)} /> : <Icon name="image" size={big ? 28 : 18} />}
    </span>
  );
}

// Every drawing style as its sample picture: the app's own first, then your own, which can be changed. Picking one closes the page.
function StylePicker({ styles, on, onPick, onClose }: { styles: PaintStyle[]; on: string; onPick: (id: string) => void; onClose: () => void }) {
  const [editing, setEditing] = useState<PaintStyle | "new" | null>(null);
  const tile = (s: PaintStyle) => (
    <div key={s.id} className={`pp-style-tile${s.id === on ? " on" : ""}`}>
      <button className="pp-style-pick" role="radio" aria-checked={s.id === on} title={s.text} onClick={() => onPick(s.id)}>
        <StyleSample style={s} big />
        <b>{s.name}</b>
        {s.id === on && <span className="tick"><Icon name="check" size={18} /></span>}
      </button>
      {s.custom && <button className="pp-style-edit" aria-label={`Change ${s.name}`} onClick={() => setEditing(s)}><Icon name="edit" size={18} /></button>}
    </div>
  );
  const mine = styles.filter(s => s.custom);
  return (
    <Layer>
      <Page className="tl-page pp">
        <TopBar onBack={onClose} title="Style" sub="How the pictures are drawn" />
        <Body>
          <Section title="Your styles" hint={mine.length ? undefined : "Describe a look of your own, see a sample, and use it for any picture."}>
            <div className="pp-style-grid" role="radiogroup" aria-label="Your styles">
              <button className="pp-style-pick new" onClick={() => setEditing("new")}>
                <span className="pp-sample big"><Icon name="plus" size={28} /></span><b>Make a style</b>
              </button>
              {mine.map(tile)}
            </div>
          </Section>
          <Section title="Ready-made">
            <div className="pp-style-grid" role="radiogroup" aria-label="Ready-made styles">{styles.filter(s => !s.custom).map(tile)}</div>
          </Section>
        </Body>
        <DoneFooter onDone={onClose} />
      </Page>
      {editing && <StyleForm style={editing === "new" ? undefined : editing} onClose={() => setEditing(null)}
        onSaved={s => { setEditing(null); if (s) onPick(s.id); }} />}
    </Layer>
  );
}

// A style of your own: a name and the words that say how pictures look. Paint the sample, change the words and paint
// again until it looks right. Back saves it if the sample shows the words as they are (a new one is then picked);
// words changed since the last sample are not kept.
function StyleForm({ style, onClose, onSaved }: { style?: PaintStyle; onClose: () => void; onSaved: (s: PaintStyle | null) => void }) {
  const [name, setName] = useState(style?.name ?? "");
  const [text, setText] = useState(style?.text ?? "");
  const [sample, setSample] = useState<{ src: string; text: string } | undefined>(style?.sample ? { src: style.sample, text: style.text } : undefined);
  const [painting, setPainting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const words = text.trim();
  const current = !!sample && sample.text === words; // the sample shows these exact words
  async function paintSample() {
    setPainting(true);
    setError(null);
    try { const pic = await api.styleSample(words); setSample({ src: pic.src, text: words }); } catch (e) { setError((e as Error).message); }
    setPainting(false);
  }
  async function save(remove = false) {
    const sameWords = !!style && style.text === words;
    const s: PaintStyle = { id: style?.id ?? uid("st"), name: name.trim() || "My style", text: words, ...(sample && current ? { sample: sample.src } : {}), ...(sameWords && style.faces ? { faces: style.faces } : {}), custom: true };
    setBusy(true);
    try {
      await saveCustomStyle(s, remove);
      if (!remove && !s.faces) void makeStyleFaces(s); // its face set, drawn in the background
      onSaved(remove || style ? null : s);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  }
  const why = painting ? "" : !words ? "Describe the style first." : !current ? (sample ? "Draw the sample again to see your changes." : "Draw a sample to see it first.") : "";
  return (
    <Layer>
      <Page className="tl-page pp">
        <TopBar onBack={busy ? undefined : () => (!why && !painting ? void save() : onClose())} title={style ? style.name : "New style"} sub="Style" />
        <Body>
          <Section title="Name">
            <input className="input big" value={name} maxLength={MAX_STYLE_NAME} aria-label="Name" placeholder="e.g. Felt tip" autoFocus={!style} onChange={e => setName(e.target.value)} />
          </Section>
          <Section title="How it looks" hint="Say what the lines, colours and shading are like. These words start every picture drawn in this style. Talking faces in this style are drawn after you save, which takes a minute or two.">
            <textarea className="input" rows={5} maxLength={MAX_STYLE_TEXT} value={text} disabled={painting} aria-label="How it looks"
              placeholder="e.g. Thick felt-tip pen outlines, bright flat colours that streak, cheap lined notebook paper." onChange={e => setText(e.target.value)} />
            <div className="pp-foot pp-foot-here">
              <ModelSelect bare />
              <button className={`btn big${current ? "" : " yellow"}`} disabled={painting || !words || current} onClick={() => void paintSample()}>
                {painting ? <><span className="spinner" /> Drawing…</> : <><Icon name="sparkle" /> {sample ? "Draw again" : "Draw a sample"}</>}
              </button>
            </div>
            {why && <p className="hint">{why}</p>}
          </Section>
          {(sample || painting) && (
            <Section title="Sample" hint="A person and their dog on a beach, the same picture for every style.">
              <div className="pp-top">
                <div className="frame-box pp-frame pp-style-frame" style={{ aspectRatio: 9 / 16 }}>
                  {sample && <img src={imageSrc(sample.src)} alt="The sample in this style" draggable={false} />}
                  {painting && <div className="frame-busy"><span className="spinner big light" /><b>Drawing…</b><span>This takes up to a minute.</span></div>}
                </div>
              </div>
            </Section>
          )}
          {style && <Section><button className="btn danger wide" disabled={busy || painting} onClick={() => setDeleting(true)}>Delete {style.name}</button></Section>}
        </Body>
        <DoneFooter disabled={busy} onDone={() => (!why && !painting ? void save() : onClose())} />
        {error && <Toast onClick={() => setError(null)}>{error}</Toast>}
      </Page>
      {deleting && style && <Confirm title={`Delete ${style.name}?`} yes="Delete" danger onYes={() => { setDeleting(false); void save(true); }} onNo={() => setDeleting(false)} />}
    </Layer>
  );
}

// One group's full page: every item to pick from, newest first as the caller orders them, and a "New …" tile.
export function RefPicker({ group: g, refs, onToggle, onNew, onEdit, onClose }: {
  group: MakerGroup; refs: PaintSource[]; onToggle: (r: PaintSource) => void; onNew?: (kind: AssetKind) => void; onEdit?: (r: PaintSource) => void; onClose: () => void;
}) {
  const copy = g.kind ? COPY[g.kind] : undefined;
  const canNew = !!(onNew && g.kind);
  const items = g.items.filter((r, i) => g.items.findIndex(x => sameRef(x, r)) === i);
  const count = refs.filter(r => items.some(x => sameRef(x, r))).length;
  return (
    <Layer>
      <Page className="tl-page pp">
        <TopBar onBack={onClose} title={g.title} sub={count ? `${count} picked` : "Tap to pick"} />
        <Body>
          {!items.length && copy && (canNew && copy.empty
            ? <div className="pp-invite"><p>{copy.empty}</p><button className="btn" onClick={() => onNew!(g.kind!)}><Icon name="plus" size={20} /> Make a {copy.noun}</button></div>
            : <p className="hint">{copy.quiet || `No ${g.title.toLowerCase()} yet.`}</p>)}
          <div className="pp-grid">
            {canNew && (items.length > 0 || !copy!.empty) && (
              <button className="pick new pp-tile" onClick={() => onNew!(g.kind!)}>
                <span className="pick-art tl-pick-art"><Icon name="plus" size={22} /></span><b>New {copy!.noun}</b>
              </button>
            )}
            {items.map(r => <RefTile key={`${r.kind}-${r.src}`} r={r} on={refs.some(x => sameRef(x, r))} disabled={false} onToggle={onToggle} onEdit={onEdit} />)}
          </div>
        </Body>
        <DoneFooter onDone={onClose} />
      </Page>
    </Layer>
  );
}

// A picked tile has a pencil (if the caller handles `onEdit`) to open the character, place or prop's own form.
function RefTile({ r, on, disabled, onToggle, onEdit }: { r: PaintSource; on: boolean; disabled: boolean; onToggle: (r: PaintSource) => void; onEdit?: (r: PaintSource) => void }) {
  return (
    <div className="pp-ref-tile">
      <button className={`pick pp-tile${on ? " on" : ""}`} aria-pressed={on} disabled={disabled} onClick={() => onToggle(r)}>
        <span className="pick-art tl-pick-art"><img className="pick-img" src={imageSrc(r.src)} alt="" /></span>
        <b>{r.name}</b>
        {on && <span className="tick"><Icon name="check" size={18} /></span>}
      </button>
      {on && onEdit && <button className="pp-tile-edit" aria-label={`Change ${r.name}`} disabled={disabled} onClick={() => onEdit(r)}><Icon name="edit" size={18} /></button>}
    </div>
  );
}
